import { useConvex } from "convex/react";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  ANALYTICS_BUS_EVENT,
  AnalyticsEventInput,
  beginTrackedEvent,
  emitAnalyticsEvent,
  getCachedVisitorLocation,
  getCampaignContext,
  getDeviceInfo,
  getReferrer,
  getVisitorId,
  getVisitorLocation,
} from "@/lib/analyticsClient";
import { isConvexConfigured } from "@/lib/convex";
import { beginPageDwell, configurePageDwell, endPageDwell } from "@/lib/pageDwell";
import { getRouteArtistSlug, isReservedRootPath } from "@/utils/routePaths";
import { fullPagePath, normalizePathname, routePatternFor, splitPathAndQuery } from "@/utils/routePattern";
import { safeGetJSON, safeSetJSON } from "@/utils/safeStorage";

export interface AnalyticsPageContext {
  pageTitle?: string;
  artist?: string;
  releaseKey?: string;
  releaseName?: string;
  metadata?: Record<string, unknown>;
}

const VISITED_PAGES_KEY = "truevined_analytics_page_views";
export const PAGE_VIEW_REVISIT_MS = 30_000;
const pageContextByPath = new Map<string, AnalyticsPageContext>();

const readVisitedPages = (): Record<string, number> => {
  const parsed = safeGetJSON<unknown>(VISITED_PAGES_KEY, "session");
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, number>) : {};
};

const writeVisitedPages = (pages: Record<string, number>) => {
  safeSetJSON(VISITED_PAGES_KEY, pages, "session");
};

export function shouldRecordPageView(pages: Record<string, number>, path: string, now: number): boolean {
  const last = pages[path];
  return typeof last !== "number" || !Number.isFinite(last) || now - last >= PAGE_VIEW_REVISIT_MS;
}

const inferArtistFromPath = (pathname: string): string | undefined => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "truevined";
  if (["admin", "newsletter", "members", "branches"].includes(parts[0])) return undefined;
  if (isReservedRootPath(parts[0])) return "truevined";
  return getRouteArtistSlug(parts);
};

export function setAnalyticsPageContext(pathname: string, context: AnalyticsPageContext) {
  const previous = pageContextByPath.get(pathname) ?? {};
  pageContextByPath.set(pathname, {
    pageTitle: context.pageTitle ?? previous.pageTitle,
    artist: context.artist ?? previous.artist,
    releaseKey: context.releaseKey ?? previous.releaseKey,
    releaseName: context.releaseName ?? previous.releaseName,
    metadata: { ...(previous.metadata ?? {}), ...(context.metadata ?? {}) },
  });
}

export function useAnalyticsPageContext(context: AnalyticsPageContext | null) {
  const location = useLocation();
  const serialized = context ? JSON.stringify(context) : "";
  useEffect(() => {
    if (!context) return;
    setAnalyticsPageContext(location.pathname, context);
  }, [location.pathname, serialized]);
}

export function useAnalyticsTracker() {
  const convex = useConvex();
  const location = useLocation();
  const lastPathRef = useRef<string>("");
  const enabled = isConvexConfigured && !(import.meta.env.DEV && import.meta.env.VITE_DISABLE_ANALYTICS === "1");

  const sendEvent = useCallback(
    async (input: AnalyticsEventInput & { eventType?: "page_view" | "custom"; pagePath?: string; pageTitle?: string; referrer?: string }) => {
      if (!enabled) return null;
      const device = getDeviceInfo();
      const { sessionId, allowed } = beginTrackedEvent({ consume: !device.isBot });
      if (!allowed) return null;
      const requested = input.pagePath ?? fullPagePath(location.pathname, window.location.search);
      const { path: pathname, search } = splitPathAndQuery(requested);
      const pagePath = fullPagePath(pathname, search);
      const routePattern = routePatternFor(pathname);
      const context = pageContextByPath.get(normalizePathname(pathname)) ?? {};
      const artist = input.artist ?? context.artist ?? inferArtistFromPath(pathname);
      const campaign = getCampaignContext(search);
      const userLocation = input.immediate ? getCachedVisitorLocation() : await getVisitorLocation();
      try {
        const result = await convex.mutation(api.analytics.trackEvent, {
          sessionId,
          adId: campaign.adId,
          visitorId: getVisitorId(),
          eventType: input.eventType ?? "custom",
          eventCategory: input.eventCategory ?? "user_action",
          eventName: input.eventName,
          pagePath,
          routePattern,
          pageTitle: input.pageTitle ?? context.pageTitle ?? document.title,
          artist: artist ? artist.toLowerCase() : undefined,
          releaseKey: input.releaseKey ?? context.releaseKey,
          releaseName: input.releaseName ?? context.releaseName,
          trackId: input.trackId,
          trackName: input.trackName,
          platform: input.platform,
          userLocation,
          deviceInfo: device,
          metadata: {
            ...(context.metadata ?? {}),
            ...(input.metadata ?? {}),
            search: search || undefined,
            adMode: campaign.adMode || undefined,
            adModeSession: campaign.adModeSession || undefined,
            ...(campaign.utm ?? {}),
          },
          referrer: input.referrer,
          duration: input.duration,
        });
        return result.eventId ?? null;
      } catch (error) {
        console.error("[Analytics] Failed to track event:", error);
        return null;
      }
    },
    [convex, enabled, location.pathname],
  );

  useEffect(() => {
    if (!enabled) return;
    configurePageDwell((eventId, durationMs) => {
      convex.mutation(api.analytics.recordPageViewDuration, { eventId: eventId as Id<"analyticsEvents">, durationMs }).catch(() => undefined);
    });
  }, [convex, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const currentPath = fullPagePath(location.pathname, location.search);
    if (currentPath === lastPathRef.current) return;
    lastPathRef.current = currentPath;
    endPageDwell(currentPath);
    if (location.pathname.startsWith("/admin")) return;
    if (!shouldRecordPageView(readVisitedPages(), currentPath, Date.now())) return;
    const timer = window.setTimeout(() => {
      void sendEvent({
        eventType: "page_view",
        eventCategory: "navigation",
        eventName: "page_view",
        pagePath: currentPath,
        referrer: getReferrer(),
      }).then((eventId) => {
        if (!eventId) return;
        writeVisitedPages({ ...readVisitedPages(), [currentPath]: Date.now() });
        if (lastPathRef.current === currentPath && !getDeviceInfo().isBot) {
          beginPageDwell(eventId, currentPath);
        }
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [enabled, location.pathname, location.search, sendEvent]);

  useEffect(() => {
    if (!enabled) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<AnalyticsEventInput>).detail;
      if (!detail?.eventName) return;
      const { onSent, ...rest } = detail;
      void sendEvent(rest).then((eventId) => onSent?.(eventId));
    };
    window.addEventListener(ANALYTICS_BUS_EVENT, handler);
    return () => window.removeEventListener(ANALYTICS_BUS_EVENT, handler);
  }, [enabled, sendEvent]);
}

export function useAnalytics() {
  const track = useCallback((eventName: string, options?: Omit<AnalyticsEventInput, "eventName">) => {
    emitAnalyticsEvent({ eventName, ...(options ?? {}) });
  }, []);
  return { track };
}
